/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { IChartSeries } from '../PerformanceGraph/IPerformanceGraph';
import moment from 'moment';
import {
  DailyTickInterval,
  DAILY_HOURS,
  DAILY_MOMENT_VALUE,
  HourlyTickInterval,
  HOUR_MOMENT_VALUE,
  singleDayTickInterval
} from './Constants';

export const getFormattedConspDetails = (chartSeries: IChartSeries) => {
  return {
    actualConsumptionData: getPerformationAgreegationdata(
      chartSeries.actualConsumptionData
    ),
    baselineConsumptionData: getPerformationAgreegationdata(
      chartSeries.baselineConsumptionData
    ),
    savedActuals: chartSeries.actualConsumptionData,
    savedBaseline: chartSeries.baselineConsumptionData
  };
};
const getPerformationAgreegationdata = (
  agreegation: [number, number][]
): [number, number][] => {
  return agreegation.map((GraphPoint: [number, number]) => {
    return [GraphPoint[0], GraphPoint[1] === null ? 0 : GraphPoint[1]];
  });
};
export const getTooltipValue = (
  x: number,
  y: number,
  original: number[][],
  DisplayUnit: string
) => {
  const value = original.find((org) => org[0] === x && org[1] === y);
  if (value) {
    return `${y} ${DisplayUnit}`;
  } else {
    return '--';
  }
};
// to get plotting data to always show 24hr on x-axis with 2hr time interval
export const getPerformaceGraphData = (
  startTime: string,
  baselineConsumptionData: [number, number][],
  actualConsumptionData: [number, number][]
) => {
  const startDateValue = moment(startTime).valueOf();
  const allDateValues = baselineConsumptionData.map((consupData) => {
    return consupData[0];
  });
  const allActualDateValues = actualConsumptionData.map((consupData) => {
    return consupData[0];
  });
  let addHour = 0;
  for (let i = 0; i < DAILY_HOURS; i++) {
    if (!allDateValues.includes(startDateValue + addHour)) {
      baselineConsumptionData.push([
        startDateValue + addHour,
        (null as unknown) as number
      ]);
    }
    if (!allActualDateValues.includes(startDateValue + addHour)) {
      actualConsumptionData.push([
        startDateValue + addHour,
        (null as unknown) as number
      ]);
    }
    addHour = addHour + HOUR_MOMENT_VALUE;
  }
  baselineConsumptionData.sort(sortNestedArray);
  actualConsumptionData.sort(sortNestedArray);
  return { baselineConsumptionData, actualConsumptionData };
};

export const getTickInterval = (
  startDate: string,
  endDate: string,
  aggregationType: string
) => {
  if (aggregationType.toLowerCase() === 'hourly') {
    return HourlyTickInterval;
  } else if (
    moment(startDate).format('MMM YYYY') === moment(endDate).format('MMM YYYY')
  ) {
    return singleDayTickInterval;
  } else {
    return DailyTickInterval;
  }
};

export const getDailyPerformaceGraphData = (
  startTime: string,
  endTime: string,
  baselineConsumptionData: [number, number][],
  actualConsumptionData: [number, number][]
) => {
  let startDateMomentValue = moment(
    moment(startTime)
      .startOf('month')
      .format('YYYY-MM-DD')
  ).valueOf();
  const endDateMomentValue = moment(
    moment(endTime)
      .endOf('month')
      .format('YYYY-MM-DD')
  ).valueOf();
  const baselineDateValues = getDateValuesFromArray(baselineConsumptionData);
  const actualDateValues = getDateValuesFromArray(actualConsumptionData);
  while (startDateMomentValue <= endDateMomentValue) {
    if (!baselineDateValues.includes(startDateMomentValue)) {
      baselineConsumptionData.push([
        startDateMomentValue,
        (null as unknown) as number
      ]);
    }
    if (!actualDateValues.includes(startDateMomentValue)) {
      actualConsumptionData.push([
        startDateMomentValue,
        (null as unknown) as number
      ]);
    }
    startDateMomentValue = startDateMomentValue + DAILY_MOMENT_VALUE;
  }
  baselineConsumptionData.sort(sortNestedArray);
  actualConsumptionData.sort(sortNestedArray);
  return { baselineConsumptionData, actualConsumptionData };
};
const getDateValuesFromArray = (consupArray: [number, number][]) => {
  return consupArray.map((consupData) => {
    return consupData[0];
  });
};
const sortNestedArray = (num1: [number, number], num2: [number, number]) => {
  return num1[0] - num2[0];
};
export const getFormattedDate = (
  aggregationType: string,
  date: number,
  lng: string = 'en-US'
) => {
  if (aggregationType === 'daily') {
    return `${moment(date)
      .locale(lng)
      .format('dddd, MMM DD, YYYY')}`;
  } else {
    return `${moment(date)
      .locale(lng)
      .format('dddd, MMM DD, HH:mm')}`;
  }
};
